import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import Logo from '../components/Logo';
import './Subscription.css';

const Subscription = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionDoc, setSubscriptionDoc] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);
  const [priceDetails, setPriceDetails] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        fetchUserSubscription(currentUser.uid);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchUserSubscription = async (userId) => {
    try {
      setLoading(true);
      setError('');

      // Fetch user document to get subscription info
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        if (userData.subscription_status && userData.subscription_type) {
          const subscriptionData = {
            status: userData.subscription_status,
            type: userData.subscription_type,
            planId: userData.subscription_plan_id,
            priceId: userData.subscription_price_id,
            quantity: userData.subscription_quantity,
            currentPeriodEnd: userData.subscription_current_period_end,
            currentPeriodStart: userData.subscription_current_period_start,
            subscriptionId: userData.subscription_id
          };
          
          setSubscription(subscriptionData);

          // Fetch subscription document if it exists
          if (subscriptionData.subscriptionId) {
            await fetchSubscriptionDocument(userId, subscriptionData.subscriptionId);
          }

          // Fetch plan and price details
          if (subscriptionData.planId) {
            await fetchPlanDetails(subscriptionData.planId, subscriptionData.priceId);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setError('Falha ao carregar detalhes da assinatura. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionDocument = async (userId, subscriptionId) => {
    try {
      const subDoc = await getDoc(doc(db, 'users', userId, 'subscriptions', subscriptionId));
      if (subDoc.exists()) {
        setSubscriptionDoc(subDoc.data());
      }
    } catch (error) {
      console.error('Error fetching subscription document:', error);
    }
  };

  const fetchPlanDetails = async (planId, priceId) => {
    try {
      // Fetch plan details
      const planDoc = await getDoc(doc(db, 'plans', planId));
      if (planDoc.exists()) {
        setPlanDetails(planDoc.data());
      }

      // Fetch price details
      if (priceId) {
        const priceDoc = await getDoc(doc(db, 'plans', planId, 'plan_prices', priceId));
        if (priceDoc.exists()) {
          setPriceDetails(priceDoc.data());
        }
      }
    } catch (error) {
      console.error('Error fetching plan/price details:', error);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription || !subscription.subscriptionId) {
      setError('Nenhuma assinatura ativa encontrada para cancelar.');
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza de que deseja cancelar sua assinatura? Sua assinatura permanecerá ativa até o final do período de cobrança atual.'
    );

    if (!confirmed) return;

    try {
      setCanceling(true);
      setError('');

      const cancelStripeSubscription = httpsCallable(functions, 'cancelStripeSubscription');
      await cancelStripeSubscription({ 
        subscriptionId: subscription.subscriptionId 
      });

      // Refresh subscription data
      await fetchUserSubscription(user.uid);
      
      alert('Sua assinatura foi cancelada com sucesso. Ela permanecerá ativa até o final do período de cobrança atual.');
    } catch (error) {
      console.error('Error canceling subscription:', error);
      setError('Falha ao cancelar assinatura. Tente novamente ou entre em contato com o suporte.');
    } finally {
      setCanceling(false);
    }
  };

  const handleUpgradeSubscription = () => {
    navigate('/subscription-plans');
  };

  const formatCurrency = (amount, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusText = (status) => {
    const statusMap = {
      'incomplete': 'Incompleto',
      'incomplete_expired': 'Incompleto (Expirado)',
      'trialing': 'Período de Teste',
      'active': 'Ativo',
      'past_due': 'Atrasado',
      'canceled': 'Cancelado',
      'unpaid': 'Não Pago',
      'paused': 'Pausado'
    };
    
    return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'trialing': return 'warning';
      case 'canceled': return 'danger';
      case 'past_due': return 'warning';
      case 'unpaid': return 'danger';
      case 'paused': return 'warning';
      case 'incomplete': return 'warning';
      case 'incomplete_expired': return 'danger';
      default: return 'secondary';
    }
  };

  if (!user) {
    return (
      <div className="subscription-page">
        <div className="container">
          <div className="message-card">
            <h1>Autenticação Necessária</h1>
            <p>Por favor, faça login para ver os detalhes da sua assinatura.</p>
            <button onClick={() => navigate('/login')} className="action-btn">
              Ir para Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="subscription-page">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
            <p>Carregando sua assinatura...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="subscription-page">
        <div className="container">
          <div className="no-subscription">
            <div className="icon">📋</div>
            <h1>Nenhuma Assinatura Ativa</h1>
            <p>Você ainda não tem uma assinatura ativa. Escolha um plano para começar!</p>
            <button onClick={handleUpgradeSubscription} className="action-btn primary">
              Ver Planos de Assinatura
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <div className="container">
        <header className="page-header">
          <h1>Gerenciamento de Assinatura</h1>
          <p>Gerencie suas configurações de assinatura e informações de cobrança</p>
        </header>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="subscription-card">
          <div className="subscription-header">
            <h2>Assinatura Atual</h2>
            <div className={`status-badge ${getStatusColor(subscription.status)}`}>
              {getStatusText(subscription.status)}
            </div>
          </div>

          <div className="subscription-details">
            <div className="detail-section">
              <h3>Informações do Plano</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Nome do Plano:</label>
                  <span>{planDetails?.name || 'Carregando...'}</span>
                </div>
                <div className="detail-item">
                  <label>Tipo de Assinatura:</label>
                  <span className="capitalize">{subscription.type}</span>
                </div>
                <div className="detail-item">
                  <label>Número de Dispositivos:</label>
                  <span>{subscription.quantity} dispositivo{subscription.quantity > 1 ? 's' : ''}</span>
                </div>
                {priceDetails && (
                  <div className="detail-item">
                    <label>Preço por Dispositivo:</label>
                    <span>
                      {formatCurrency(priceDetails.price_value)} / {priceDetails.billing_cycle}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="detail-section">
              <h3>Informações de Cobrança</h3>
              <div className="detail-grid">
                {subscription.currentPeriodStart && (
                  <div className="detail-item">
                    <label>Início do Período Atual:</label>
                    <span>{formatDate(subscription.currentPeriodStart)}</span>
                  </div>
                )}
                {subscription.currentPeriodEnd && (
                  <div className="detail-item">
                    <label>Fim do Período Atual:</label>
                    <span>{formatDate(subscription.currentPeriodEnd)}</span>
                  </div>
                )}
                {subscriptionDoc?.cancelAtPeriodEnd && (
                  <div className="detail-item">
                    <label>Status do Cancelamento:</label>
                    <span className="warning">Será cancelado no fim do período</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="subscription-actions">
            <h3>Gerenciar Assinatura</h3>
            <div className="action-buttons">
              {subscription.status === 'active' && subscription.type === 'stripe' && (
                <button
                  onClick={handleCancelSubscription}
                  disabled={canceling}
                  className="action-btn danger"
                >
                  {canceling ? (
                    <>
                      <div className="spinner small"></div>
                      Cancelando...
                    </>
                  ) : (
                    'Cancelar Assinatura'
                  )}
                </button>
              )}

              <button
                onClick={handleUpgradeSubscription}
                className="action-btn primary"
              >
                Ver Todos os Planos
              </button>
            </div>

            {subscription.type === 'manual' && (
              <div className="manual-subscription-notice">
                <p>
                  <strong>Assinatura Manual:</strong> Esta assinatura é gerenciada manualmente. 
                  Entre em contato com o suporte para alterações ou cancelamentos.
                </p>
              </div>
            )}
          </div>
        </div>

        {planDetails?.description && (
          <div className="plan-description-card">
            <h3>Sobre Seu Plano</h3>
            <p>{planDetails.description}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscription;